type LinkedTextureLike = {
  zoneName?: string
  zoneDisplayName?: string
  zone?: string
  idAcabadoUnidadModeloAcabado?: string
  name?: string
  url?: string
  color?: string
  groupID?: number
  searchID?: number
  id?: number
  linkedTextureID?: number
  repeatX?: number
  repeatY?: number
  opacity?: number
  roughness?: number
  metalness?: number
  mirror?: boolean
  envMapIntensity?: number
  bumpMap?: string | null
  bumpScale?: number
  displacement?: number
  displacementScale?: number | null
  textureRotation?: number
  complemento?: string
  description?: string
  descripcion?: string
  img?: string
  acabado?: string
  modelo?: string
}

export type SyncZonesLogContext = {
  itemId?: string
  catalogID?: string
  source?: string
}

export function findZoneKeyForTexture(
  zones: Record<string, any>,
  texture: LinkedTextureLike,
): string | null {
  if (texture.zoneName && zones[texture.zoneName]) {
    return texture.zoneName
  }

  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material
    if (!mat) continue

    if (
      texture.zoneDisplayName &&
      (mat.zoneDisplayName === texture.zoneDisplayName ||
        mat.zone === texture.zoneDisplayName ||
        key === texture.zoneDisplayName)
    ) {
      return key
    }

    if (texture.zoneName && (zone.name === texture.zoneName || key === texture.zoneName)) {
      return key
    }
  }

  return null
}

export function textureToZoneMaterial(
  texture: LinkedTextureLike,
  existingMaterial: Record<string, any> = {},
): Record<string, any> {
  return {
    ...existingMaterial,
    name: texture.name ?? existingMaterial.name,
    url: texture.url ?? existingMaterial.url,
    color: texture.color ?? existingMaterial.color,
    groupID: texture.groupID ?? existingMaterial.groupID,
    searchID: texture.searchID ?? existingMaterial.searchID,
    id: texture.id ?? existingMaterial.id,
    linkedTextureID: texture.linkedTextureID ?? existingMaterial.linkedTextureID,
    repeatX: texture.repeatX ?? existingMaterial.repeatX,
    repeatY: texture.repeatY ?? existingMaterial.repeatY,
    opacity: texture.opacity ?? existingMaterial.opacity,
    roughness: texture.roughness ?? existingMaterial.roughness,
    metalness: texture.metalness ?? existingMaterial.metalness,
    mirror: texture.mirror ?? existingMaterial.mirror,
    envMapIntensity: texture.envMapIntensity ?? existingMaterial.envMapIntensity,
    bumpMap: texture.bumpMap ?? existingMaterial.bumpMap,
    bumpScale: texture.bumpScale ?? existingMaterial.bumpScale,
    displacement: texture.displacement ?? existingMaterial.displacement,
    displacementScale: texture.displacementScale ?? existingMaterial.displacementScale,
    textureRotation: texture.textureRotation ?? existingMaterial.textureRotation,
    complemento: texture.complemento ?? existingMaterial.complemento,
    description:
      texture.description ?? texture.descripcion ?? texture.name ?? existingMaterial.description,
    img: texture.img ?? existingMaterial.img,
    acabado: texture.acabado ?? existingMaterial.acabado,
    modelo: texture.modelo ?? existingMaterial.modelo,
    zoneDisplayName: texture.zoneDisplayName ?? existingMaterial.zoneDisplayName,
    zone: texture.zone ?? texture.zoneDisplayName ?? existingMaterial.zone,
  }
}

const MATERIAL_COMPARE_FIELDS = [
  'name',
  'url',
  'envMapIntensity',
  'metalness',
  'roughness',
  'searchID',
  'id',
] as const

export function logLinkedItemMaterialSync(
  item: {
    textures?: LinkedTextureLike[]
    zones?: Record<string, any>
    catalogID?: string
  },
  context: SyncZonesLogContext,
): void {
  const mismatches: Array<Record<string, unknown>> = []

  for (const texture of item.textures ?? []) {
    const zoneKey = findZoneKeyForTexture(item.zones ?? {}, texture)
    if (!zoneKey) continue

    const mat = item.zones?.[zoneKey]?.material
    if (!mat) continue

    const diff: Record<string, { texture: unknown; zone: unknown }> = {}
    for (const field of MATERIAL_COMPARE_FIELDS) {
      const textureValue = texture[field]
      const zoneValue = mat[field]
      if (textureValue !== undefined && zoneValue !== undefined && textureValue !== zoneValue) {
        diff[field] = { texture: textureValue, zone: zoneValue }
      }
    }

    if (Object.keys(diff).length > 0) {
      mismatches.push({
        zoneKey,
        zoneDisplayName: texture.zoneDisplayName,
        textureName: texture.name,
        diff,
      })
    }
  }

  if (mismatches.length > 0) {
    console.warn('[syncLinkedItemZonesFromTextures] desajuste textures ↔ zones', {
      ...context,
      catalogID: item.catalogID ?? context.catalogID,
      mismatches,
    })
  }
}

/** Copia propiedades de item.textures a item.zones[*].material (render 3D). */
export function syncLinkedItemZonesFromTextures(
  item: {
    textures?: LinkedTextureLike[]
    zones?: Record<string, any>
    catalogID?: string
  },
  context?: SyncZonesLogContext,
): number {
  if (!item.textures?.length || !item.zones) return 0

  if (context) {
    logLinkedItemMaterialSync(item, { ...context, source: `${context.source ?? 'sync'}:before` })
  }

  let synced = 0
  for (const texture of item.textures) {
    const zoneKey = findZoneKeyForTexture(item.zones, texture)
    if (!zoneKey || !item.zones[zoneKey]?.material) continue

    item.zones[zoneKey].material = textureToZoneMaterial(texture, item.zones[zoneKey].material)

    if (texture.idAcabadoUnidadModeloAcabado) {
      item.zones[zoneKey].idAcabadoUnidadModeloAcabado = texture.idAcabadoUnidadModeloAcabado
    }

    synced++
  }

  if (context) {
    logLinkedItemMaterialSync(item, { ...context, source: `${context.source ?? 'sync'}:after` })
  }

  return synced
}
